/**
 * Layout админ-панели. На desktop - постоянный левый sidebar.
 * На mobile - hamburger в шапке открывает slide-left drawer с теми же
 * пунктами. Контент рендерится через <Outlet/>. Защищён <RequireAdmin/>.
 */
import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import * as RDialog from "@radix-ui/react-dialog";
import {
  LayoutDashboard, Users, Sparkles, CreditCard, Tag, Share2,
  MessageSquare, AlertTriangle, ArrowLeft, LogOut, Wallet, Menu, X, Lightbulb,
} from "lucide-react";
import { Avatar } from "@/components/ui/Avatar";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/stores/auth";
import { cn } from "@/lib/cn";

const NAV = [
  { to: "/admin",              label: "Дашборд",      icon: LayoutDashboard, end: true },
  { to: "/admin/users",        label: "Пользователи", icon: Users },
  { to: "/admin/models",       label: "Модели",       icon: Sparkles },
  { to: "/admin/templates",    label: "Шаблоны",      icon: Lightbulb },
  { to: "/admin/credit-packs", label: "Пакеты",       icon: CreditCard },
  { to: "/admin/promos",       label: "Промокоды",    icon: Tag },
  { to: "/admin/referrals",    label: "Рефералы",     icon: Share2 },
  { to: "/admin/tickets",      label: "Заявки",       icon: MessageSquare },
  { to: "/admin/payments",     label: "Платежи",      icon: Wallet },
  { to: "/admin/errors",       label: "Ошибки",       icon: AlertTriangle },
] as const;

export function RequireAdmin({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  if (user === null) return null;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function AdminLayout() {
  const navigate = useNavigate();
  const logout = useAuth((s) => s.logout);
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <RequireAdmin>
      <div className="min-h-screen flex bg-bg">
        {/* Desktop sidebar - тот же, что и был, но скрыт на mobile */}
        <aside className="hidden lg:flex w-60 shrink-0 border-r border-line-soft bg-surface flex-col">
          {/* Логотип кликом ведёт на главную страницу. */}
          <NavLink to="/" className="px-4 py-4 inline-block rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand">
            <Logo size={24} subtitle="Админка" />
          </NavLink>
          <nav className="flex-1 px-2 space-y-0.5">
            <NavItems />
          </nav>
          <FooterButtons onNavigate={navigate} onLogout={logout} />
        </aside>

        <div className="flex flex-1 min-w-0 flex-col">
          {/* Mobile header с hamburger */}
          <AdminMobileHeader onOpenNav={() => setDrawerOpen(true)} />

          <main className="flex-1 min-w-0 overflow-x-hidden">
            <Outlet />
          </main>
        </div>

        {/* Mobile drawer */}
        <AdminMobileDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
      </div>
    </RequireAdmin>
  );
}

// ---------------------------------------------------------------------------
function NavItems({ onItemClick }: { onItemClick?: () => void } = {}) {
  return (
    <>
      {NAV.map((n) => {
        const Icon = n.icon;
        return (
          <NavLink
            key={n.to}
            to={n.to}
            end={"end" in n && n.end}
            onClick={onItemClick}
            className={({ isActive }) => cn(
              "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition",
              isActive ? "bg-brand-50 text-brand-700 font-medium"
                       : "text-ink-mute hover:bg-surface-2 hover:text-ink",
            )}
          >
            <Icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        );
      })}
    </>
  );
}

function FooterButtons({
  onNavigate, onLogout,
}: { onNavigate: (to: string) => void; onLogout: () => void }) {
  return (
    <div className="px-3 pb-4 space-y-1.5">
      <Button variant="ghost" className="w-full justify-start gap-3 px-3 text-ink-soft"
              onClick={() => onNavigate("/chat/new")}>
        <ArrowLeft className="h-4 w-4" />
        В кабинет
      </Button>
      <Button variant="ghost" className="w-full justify-start gap-3 px-3 text-ink-soft"
              onClick={onLogout}>
        <LogOut className="h-4 w-4" />
        Выйти
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile-only header: hamburger слева, Logo по центру, avatar справа
// ---------------------------------------------------------------------------
function AdminMobileHeader({ onOpenNav }: { onOpenNav: () => void }) {
  const user = useAuth((s) => s.user);
  const initials = (user?.display_name || user?.email || "?").slice(0, 2).toUpperCase();
  return (
    <header className="lg:hidden sticky top-0 z-20 flex h-14 items-center justify-between bg-surface/70 backdrop-blur px-4 border-b border-line-soft">
      <button
        type="button"
        onClick={onOpenNav}
        aria-label="open admin menu"
        className="grid h-9 w-9 place-items-center rounded-lg text-ink hover:bg-surface-2"
      >
        <Menu className="h-5 w-5" />
      </button>
      <div className="text-sm font-semibold text-ink">Админка</div>
      <Avatar initials={initials} size="sm" />
    </header>
  );
}

// ---------------------------------------------------------------------------
// Mobile drawer - копия sidebar в slide-left панели
// ---------------------------------------------------------------------------
function AdminMobileDrawer({
  open, onOpenChange,
}: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate();
  const logout = useAuth((s) => s.logout);
  const location = useLocation();

  // Закрываем при смене маршрута (юзер тапнул по пункту)
  useEffect(() => {
    onOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="lg:hidden fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm data-[state=open]:animate-fade-in" />
        <RDialog.Content
          className={cn(
            "lg:hidden fixed inset-y-0 left-0 z-50 w-[82vw] max-w-[300px]",
            "bg-surface border-r border-line-soft shadow-pop flex flex-col",
            "data-[state=open]:animate-slide-up",
          )}
        >
          <RDialog.Title className="sr-only">Админ-меню</RDialog.Title>

          <div className="flex items-center justify-between px-4 pt-4 pb-3">
            {/* Логотип кликом ведёт на главную + закрывает drawer. */}
            <button
              type="button"
              onClick={() => { onOpenChange(false); navigate("/"); }}
              className="inline-flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <Logo size={24} subtitle="Админка" />
            </button>
            <RDialog.Close className="grid h-9 w-9 place-items-center rounded-lg text-ink-mute hover:bg-surface-2">
              <X className="h-5 w-5" />
            </RDialog.Close>
          </div>

          <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto">
            <NavItems />
          </nav>

          <FooterButtons
            onNavigate={(to) => { onOpenChange(false); navigate(to); }}
            onLogout={() => { onOpenChange(false); logout(); }}
          />
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}
